import { createContext, useCallback, useContext, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
type Task = { id: number; label: string };
export const yieldToPaint = () => new Promise<void>((resolve) => {
  if (document.hidden) { setTimeout(resolve, 0); return; }
  requestAnimationFrame(() => setTimeout(resolve, 0));
});
const ActivityContext = createContext({ run: async <T,>(_label: string, operation: () => Promise<T> | T): Promise<T> => operation() });
export function ActivityProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([]), sequence = useRef(0);
  const run = useCallback(async <T,>(label: string, operation: () => Promise<T> | T): Promise<T> => {
    const id = ++sequence.current;
    setTasks((current) => [...current, { id, label }]);
    try { await yieldToPaint(); return await operation(); }
    finally { setTasks((current) => current.filter((task) => task.id !== id)); }
  }, []);
  return <ActivityContext.Provider value={{ run }}>{children}{tasks.length > 0 && <div className="fixed bottom-4 left-1/2 z-[100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-primary/30 bg-background p-4 shadow-lg"><LoadingStatus label={tasks[tasks.length-1].label} /></div>}</ActivityContext.Provider>;
}
export function useActivity() { return useContext(ActivityContext); }
export function LoadingStatus({ label }: { label: string }) {
  return <div role="status" aria-live="polite" className="flex min-w-0 items-center gap-3 text-sm"><LoaderCircle aria-hidden="true" className="size-5 shrink-0 animate-spin motion-reduce:animate-none" /><span className="min-w-0 break-words font-medium">{label}</span></div>;
}
